import { useState } from 'react';
import { useWeb3React } from '@web3-react/core';
import { Web3Provider } from '@ethersproject/providers';
import { ethers, Contract } from 'ethers';
import { motion } from 'framer-motion';
import managerABI from '../../contracts/tncGame/managerv1/ABI';
import arrowUpIcon from '../../constants/icons/arrow_up_icon.png';

interface IProps {
  forcePageRefresh: React.MutableRefObject<boolean>;
  createGameBlock: React.MutableRefObject<number>;
}

const TnCLobbyCreate: React.FC<IProps> = ({
  forcePageRefresh,
  createGameBlock
}: IProps) => {
  // states and contexts
  const [expanded, setExpanded] = useState<boolean>(false);
  const [winScore, setWinScore] = useState<number>(3);
  const [fieldLength, setFieldLength] = useState<number>(50);
  const { account, library } = useWeb3React<Web3Provider>();

  // contract
  const provider = new ethers.providers.StaticJsonRpcProvider(
    'https://data-seed-prebsc-1-s1.binance.org:8545/'
  );

  const managerAddress = process.env.REACT_APP_TNCGAME_MANAGER_ADDRESS;
  const contract = new Contract(
    `${managerAddress}`,
    managerABI.ABI,
    library?.getSigner() ? library?.getSigner() : provider
  );

  // boundary conditions
  const minWinScore = 2;
  const maxWinScore = 10;
  const minFieldLength = 50;
  const maxFieldLength = 1000;

  // UI functions
  const onCreate = async () => {
    if (account) {
      if (!expanded) {
        setExpanded(true);
      } else if (
        winScore >= minWinScore &&
        winScore <= maxWinScore &&
        fieldLength >= minFieldLength &&
        fieldLength <= maxFieldLength
      ) {
        try {
          const receipt = await contract.createGame(
            fieldLength * 1000,
            winScore
          );
          const awaitedReceipt = await receipt.wait();
          createGameBlock.current = awaitedReceipt.blockNumber;
          forcePageRefresh.current = true;
        } catch (error: any) {
          if (error.data) {
            window.alert(error.data.message);
          } else {
            window.alert(error.message);
          }
        }
      }
    }
  };

  return (
    <div className="tncLobbyAccordionWrapper">
      <motion.div
        key="tncLobbyCreateForm"
        initial="collapsed"
        animate={expanded ? 'open' : 'closed'}
        exit="collapsed"
        variants={{
          open: { opacity: 1, height: 'auto' },
          collapsed: { opacity: 0, height: 0 }
        }}
        transition={{ duration: 0.8, ease: [0.04, 0.62, 0.23, 0.98] }}
      >
        <div className="tncLobbyAccordionTitle">Create a New Contract</div>
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="tncLobbyCollapseButton"
        >
          <img src={arrowUpIcon} alt="arrowUpIcon" />
        </button>
        <div className="tncLobbyAccordionContainer">
          <div className="tncLobbyAccordion">
            <span>WIN SCORE</span>
            <div className="tncLobbyAccordionInputWrapper">
              <input
                type="number"
                step="any"
                min={minWinScore}
                placeholder="3"
                onChange={(e) => setWinScore(parseFloat(e.target.value))}
              />
            </div>
          </div>
          <div className="tncLobbyAccordion">
            <span>FIELD LENGTH</span>
            <div className="tncLobbyAccordionInputWrapper">
              <input
                type="number"
                step="any"
                min={minFieldLength}
                placeholder="50"
                onChange={(e) => setFieldLength(parseFloat(e.target.value))}
              />
              <span>m</span>
            </div>
          </div>
        </div>
      </motion.div>
      <button
        className="tncLobbyCreateButton"
        type="button"
        disabled={!account}
        onClick={onCreate}
      >
        CREATE
      </button>
    </div>
  );
};

export default TnCLobbyCreate;
